// Chatbot.jsx
import {
  useEffect,
  useRef,
  useState,
} from 'react';

import { ArrowLeft } from 'lucide-react';

import TypingIndicatorBubble from './TypingIndicator';
// Import du fichier CSS module principal du chatbot
import styles from './Chatbot.module.css';

// Importez la bulle de 3 points (typing indicator)

function Chatbot() {
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isTyping, setIsTyping] = useState(false);

  // Référence pour le bloc de chat (pour l'auto-scroll)
  const chatHistoryRef = useRef(null);

  // À chaque mise à jour du chatHistory ou de isTyping, on scrolle vers le bas
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [chatHistory, isTyping]);

  // --- Catégories et questions ---
  const categories = {
    "📌 Services": [
      "Quels services proposez-vous pour faciliter mon investir en Algérie ?",
      "Quels sont les avantages de votre service 'Facilité administrative' ?",
    ],
    "💼 Packs d'expatriation": [
      "En quoi consiste le pack 'Conseils juridiques téléphoniques' ?",
      "Que comprend le pack 'Recherche immobilière' ?",
      "Quels sont les détails du service 'Véritable assistant' ?",
      "Que comprend le pack 'Accompagnant administratif' ?",
      "Combien coûte le pack 'Accompagnant administratif' ?",
      "Quelle est la différence entre le pack 'Recherche immobilière' et le pack 'Véritable assistant' ?",
    ],
    "📞 Contact & Réseaux": [
      "Comment puis-je contacter BKBO pour plus d'informations ?",
      "Quels réseaux sociaux BKBO utilise-t-il ?",
    ]
  };

  // --- Réponses associées ---
  const responses = {
    "Quels services proposez-vous pour faciliter mon expatriation en Algérie ?":
      "Nous offrons une assistance administrative complète, de la création légale d'une entreprise à la mise en place de structures fonctionnelles. Nous nous occupons également de la recherche de logement et d'autres démarches importantes.",

    "Quels sont les avantages de votre service 'Facilité administrative' ?":
      "Avec notre service 'Facilité administrative', nous gérons toutes les procédures complexes comme la création légale, la constitution juridique et la mise en place de la structure fonctionnelle avec les partenaires (banque, comptable, avocats, assurance).",

    "En quoi consiste le pack 'Conseils juridiques téléphoniques' ?":
      "Le pack 'Conseils juridiques téléphoniques' inclut une assistance téléphonique avec votre juriste dédié, avocat ou notaire pour  des conseils et orientations.",

    "Que comprend le pack 'Recherche immobilière' ?":
      "Le pack 'Recherche immobilière' aide à la recherche et l'acquisition de biens immobiliers.",
    "Quels sont les détails du service 'Véritable assistant' ?":
      "Accompagnement complet pour les familles souhaitant s'installer en Algérie, de l'arrivée à l'installation.\n, Logement adapté\n, Inscription des enfants à l'école\n, Démarches administratives\n, Accompagnement sur place",
    "Que comprend le pack 'Accompagnant administratif' ?":
      "Accompagnement dédié pour structurer et concrétiser vos projets d'investissement en Algérie. Analyse du projet, dossiers administratifs, suivi des démarches, mise en relation avec des experts.",
    "Combien coûte le pack 'Accompagnant administratif' ?":
      "Le prix du pack 'Accompagnant administratif' est uniquement sur devis. Veuillez nous contacter pour plus de détails.",
    "Quelle est la différence entre le pack 'Recherche immobilière' et le pack 'Véritable assistant' ?":
      "Le Pack Recherche Immobilière sert uniquement à trouver un bien selon vos critères. Le Pack Véritable Assistant va plus loin : il vous accompagne dans toute votre installation en Algérie (logement, démarches, école, installation).",
    "Comment puis-je contacter BKBO pour plus d'informations ?":
      "Vous pouvez nous contacter par email à contact@bkbo-expat.com ou par téléphone au 0033 7 59 73 12 23. Notre adresse est 05 Boulevard Mellah Ali Hai Sidi El Bachir ex Marceau Plateau Oran, 31021.",

    "Quels réseaux sociaux BKBO utilise-t-il ?":
      "Vous pouvez nous suivre sur Facebook, Instagram et Tiktok pour les dernières mises à jour et nouvelles.",

    // Fallback si la question n'est pas reconnue
    fallback: "Je ne suis pas sûr de comprendre votre question. Pouvez-vous reformuler ?"
  };

  // --- Gère le clic sur une catégorie ---
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  // --- Retour à la liste des catégories ---
  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  // --- Gère le clic sur une question ---
  const handleQuestionClick = (question) => {
    // Ajoute d'abord le message de l'utilisateur
    const userMessage = {
      type: 'user',
      text: question,
    };
    setChatHistory((prev) => [...prev, userMessage]);

    // Active l'indicateur de frappe
    setIsTyping(true);

    // Simule un léger délai avant de donner la réponse
    setTimeout(() => {
      const answer = responses[question] || responses.fallback;
      const botMessage = { type: 'bot', text: answer };
      setChatHistory((prev) => [...prev, botMessage]);
      setIsTyping(false);
    }, 1200);
  };

  return (
    <div className={styles.chatbotContainer}>
      {/* 1) Zone historique des messages */}
      <div className={styles.chatHistory} ref={chatHistoryRef}>
        {chatHistory.map((entry, index) => (
          <div key={index} className={styles.chatEntry}>
            {entry.type === 'user' ? (
              <div className={styles.userMessage}>
                <div className={`${styles.messageBox} ${styles.user}`}>
                  <p className={styles.messageText}>{entry.text}</p>
                </div>
              </div>
            ) : (
              <div className={styles.botMessage}>
                <div className={`${styles.messageBox} ${styles.bot}`}>
                  <p className={styles.messageText}>{entry.text}</p>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* 2) Indicateur si le bot "tape" */}
        {isTyping && (
          <div className={styles.botMessage}>
            <div className={`${styles.messageBox} ${styles.bot}`}>
              <TypingIndicatorBubble />
            </div>
          </div>
        )}
      </div>

      {/* 3) Menu des catégories ou liste de questions */}
      <div className={styles.actionsArea}>
        {selectedCategory === null ? (
          <>
            <p className={styles.menuDescription}>
              Bienvenue chez BKBO ! Sélectionnez une catégorie pour en savoir plus :
            </p>
            <div className={styles.categoriesContainer}>
              {Object.keys(categories).map((category, index) => (
                <button
                  key={index}
                  onClick={() => handleCategoryClick(category)}
                  className={styles.questionButton}
                >
                  {category}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className={styles.questionsContainer}>
            <button onClick={handleBackToCategories} className={styles.backButton}>
              <ArrowLeft /> Retour
            </button>
            {categories[selectedCategory].map((question, index) => (
              <button
                key={index}
                onClick={() => handleQuestionClick(question)}
                className={styles.questionButton}
              >
                {question}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Chatbot;